import axios from "axios";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import reqNoAuth from "../../../services/requestNoAuth";
import {
  createIntegrityEvent,
  createSnapshotFailureEvent,
  createUuid,
  IntegrityEvent,
  IntegrityEventType,
  PresenceState,
  transitionPresence,
} from "./events";

type Product = "assessment" | "psikotest";

export type IntegrityStateEvent = {
  phase: "away" | "return";
  product: Product;
  sessionId: number;
  sessionKind: string;
  clientEventId: string;
  occurredAt: string;
  durationMs?: number;
};

type Options = {
  product: Product;
  sessionId?: number;
  sessionKind?: string;
  accessKey?: string;
  running: boolean;
  onReturnSync: () => void | Promise<void>;
  onStateEvent?: (event: IntegrityStateEvent) => void;
  onTabAwayEventPersisted?: (event: IntegrityEvent) => void;
};

type ConsentRow = {
  policy_version: string;
  accepted: boolean;
  accepted_at: string | null;
};

type ConsentState = {
  ready: boolean;
  enabled: boolean;
  accepting: boolean;
  row?: ConsentRow;
  error?: string;
};

export type SessionIntegrity = {
  ready: boolean;
  enabled: boolean;
  accepted: boolean;
  accepting: boolean;
  consentError?: string;
  policyVersion?: string;
  isAway: boolean;
  warning?: string;
  fullscreenPrompt: boolean;
  acceptConsent: () => Promise<void>;
  requestFullscreen: () => Promise<void>;
  returnToFullscreen: () => Promise<void>;
  recordEvent: (
    eventType: Exclude<IntegrityEventType, "tab_away" | "tab_return">,
    metadata?: IntegrityEvent["metadata"]
  ) => void;
  recordSnapshotFailure: (
    sourceClientEventId: string,
    occurredAt: string,
    reason: string
  ) => void;
};

const unavailableMessages = [
  "doesn't exist",
  "does not exist",
  "no such table",
  "integrity feature is disabled",
];

function isCompatibilityUnavailable(error: unknown) {
  if (!axios.isAxiosError(error)) return false;
  const status = error.response?.status;
  const message = String(error.response?.data?.message ?? "").toLowerCase();
  return (
    status === 404 ||
    status === 409 ||
    (status === 400 &&
      unavailableMessages.some((candidate) => message.includes(candidate)))
  );
}

function emitStateEvent(event: IntegrityStateEvent) {
  window.dispatchEvent(
    new CustomEvent<IntegrityStateEvent>("kamara:integrity-state", {
      detail: event,
    })
  );
}

function readQueue(storageKey: string): IntegrityEvent[] {
  try {
    const value = JSON.parse(localStorage.getItem(storageKey) ?? "[]");
    return Array.isArray(value) ? value : [];
  } catch {
    return [];
  }
}

function writeQueue(storageKey: string, events: IntegrityEvent[]) {
  try {
    localStorage.setItem(storageKey, JSON.stringify(events));
  } catch {
    // A blocked/full localStorage must not interrupt a timed session.
  }
}

export default function useSessionIntegrity({
  product,
  sessionId,
  sessionKind,
  accessKey,
  running,
  onReturnSync,
  onStateEvent,
  onTabAwayEventPersisted,
}: Options): SessionIntegrity {
  const [consent, setConsent] = useState<ConsentState>({
    ready: !sessionId,
    enabled: false,
    accepting: false,
  });
  const [isAway, setIsAway] = useState(false);
  const [warning, setWarning] = useState<string>();
  const [fullscreenPrompt, setFullscreenPrompt] = useState(false);
  const enabledRef = useRef(false);
  const queueRef = useRef<IntegrityEvent[]>([]);
  const presenceRef = useRef<PresenceState>({ awaySince: null });
  const sendingRef = useRef(false);
  const retryRef = useRef(1000);
  const retryTimerRef = useRef<number>();
  const warningTimerRef = useRef<number>();
  const fullscreenArmedRef = useRef(false);
  const fullscreenStateRef = useRef(false);
  const onReturnSyncRef = useRef(onReturnSync);
  const onStateEventRef = useRef(onStateEvent);
  const onTabAwayEventPersistedRef = useRef(onTabAwayEventPersisted);

  const normalizedKind = (sessionKind ?? "session").toLowerCase();
  const storageKey = `kamara-integrity:${product}:${normalizedKind}:${
    sessionId ?? "none"
  }`;
  const basePath = `/api/${product}_participant`;
  const eventPath = `${basePath}/session/${encodeURIComponent(
    normalizedKind
  )}/${sessionId ?? ""}/integrity/events`;
  const requestConfig = useMemo(
    () =>
      product === "assessment" && accessKey
        ? { headers: { "X-Participant-Access-Key": accessKey } }
        : undefined,
    [accessKey, product]
  );

  useEffect(() => {
    enabledRef.current = consent.enabled;
  }, [consent.enabled]);

  useEffect(() => {
    onReturnSyncRef.current = onReturnSync;
    onStateEventRef.current = onStateEvent;
    onTabAwayEventPersistedRef.current = onTabAwayEventPersisted;
  }, [onReturnSync, onStateEvent, onTabAwayEventPersisted]);

  const disableCompatibilityMode = useCallback(() => {
    enabledRef.current = false;
    queueRef.current = [];
    writeQueue(storageKey, []);
    setConsent((current) => ({
      ...current,
      ready: true,
      enabled: false,
      accepting: false,
      error: undefined,
    }));
  }, [storageKey]);

  const flushQueue = useCallback(async () => {
    if (
      !enabledRef.current ||
      !sessionId ||
      sendingRef.current ||
      queueRef.current.length === 0
    ) {
      return;
    }

    const batch = queueRef.current.slice(0, 50);
    sendingRef.current = true;
    let persisted = false;
    try {
      await reqNoAuth.post(eventPath, { events: batch }, requestConfig);
      batch
        .filter((event) => event.event_type === "tab_away")
        .forEach((event) => onTabAwayEventPersistedRef.current?.(event));
      const sentIds = new Set(batch.map((event) => event.client_event_id));
      queueRef.current = queueRef.current.filter(
        (event) => !sentIds.has(event.client_event_id)
      );
      writeQueue(storageKey, queueRef.current);
      retryRef.current = 1000;
      persisted = true;
    } catch (error) {
      if (isCompatibilityUnavailable(error)) {
        disableCompatibilityMode();
      } else {
        window.clearTimeout(retryTimerRef.current);
        retryTimerRef.current = window.setTimeout(() => {
          void flushQueue();
        }, retryRef.current);
        retryRef.current = Math.min(retryRef.current * 2, 30000);
      }
    } finally {
      sendingRef.current = false;
      if (
        persisted &&
        enabledRef.current &&
        queueRef.current.length > 0
      ) {
        void flushQueue();
      }
    }
  }, [
    disableCompatibilityMode,
    eventPath,
    requestConfig,
    sessionId,
    storageKey,
  ]);

  const enqueue = useCallback(
    (events: IntegrityEvent[]) => {
      if (!enabledRef.current || events.length === 0) return;
      const known = new Set(
        queueRef.current.map((event) => event.client_event_id)
      );
      queueRef.current = queueRef.current.concat(
        events.filter((event) => !known.has(event.client_event_id))
      );
      writeQueue(storageKey, queueRef.current);
      void flushQueue();
    },
    [flushQueue, storageKey]
  );

  const showWarning = useCallback((message: string) => {
    setWarning(message);
    window.clearTimeout(warningTimerRef.current);
    warningTimerRef.current = window.setTimeout(
      () => setWarning(undefined),
      2500
    );
  }, []);

  const recordEvent = useCallback(
    (
      eventType: Exclude<IntegrityEventType, "tab_away" | "tab_return">,
      metadata: IntegrityEvent["metadata"] = {}
    ) => {
      enqueue([createIntegrityEvent(eventType, metadata)]);
    },
    [enqueue]
  );

  const recordSnapshotFailure = useCallback(
    (sourceClientEventId: string, occurredAt: string, reason: string) => {
      enqueue([
        createSnapshotFailureEvent(sourceClientEventId, occurredAt, reason),
      ]);
    },
    [enqueue]
  );

  useEffect(() => {
    let cancelled = false;
    // Only reset presence when the session identity changes — not when flushQueue
    // identity churns. Clearing mid-away lets blur/visibility fire a fresh tab_away.
    presenceRef.current = { awaySince: null };
    setIsAway(false);
    fullscreenArmedRef.current = false;
    fullscreenStateRef.current = Boolean(document.fullscreenElement);
    setFullscreenPrompt(false);
    window.clearTimeout(retryTimerRef.current);

    if (!sessionId) {
      setConsent({ ready: true, enabled: false, accepting: false });
      return;
    }

    queueRef.current = readQueue(storageKey);
    setConsent({ ready: false, enabled: false, accepting: false });
    reqNoAuth
      .get(`${basePath}/integrity/consent`, requestConfig)
      .then((response) => {
        if (cancelled) return;
        const enabled = response.data?.feature_enabled === true;
        enabledRef.current = enabled;
        setConsent({
          ready: true,
          enabled,
          accepting: false,
          row: response.data?.row,
        });
      })
      .catch(() => {
        if (!cancelled) disableCompatibilityMode();
      });

    return () => {
      cancelled = true;
      window.clearTimeout(retryTimerRef.current);
    };
  }, [
    basePath,
    disableCompatibilityMode,
    requestConfig,
    sessionId,
    storageKey,
  ]);

  useEffect(() => {
    if (consent.enabled && sessionId) void flushQueue();
  }, [consent.enabled, flushQueue, sessionId]);

  const acceptConsent = useCallback(async () => {
    if (!consent.enabled || !consent.row?.policy_version) return;
    setConsent((current) => ({
      ...current,
      accepting: true,
      error: undefined,
    }));
    try {
      const response = await reqNoAuth.post(
        `${basePath}/integrity/consent`,
        {
          policy_version: consent.row.policy_version,
          accepted: true,
        },
        requestConfig
      );
      setConsent((current) => ({
        ...current,
        accepting: false,
        row: response.data?.row,
      }));
    } catch (error) {
      if (isCompatibilityUnavailable(error)) {
        disableCompatibilityMode();
        return;
      }
      setConsent((current) => ({
        ...current,
        accepting: false,
        error: "Persetujuan belum tersimpan. Periksa koneksi, lalu coba lagi.",
      }));
    }
  }, [
    basePath,
    consent.enabled,
    consent.row?.policy_version,
    disableCompatibilityMode,
    requestConfig,
  ]);

  const requestFullscreen = useCallback(async () => {
    if (!enabledRef.current || document.fullscreenElement) return;
    fullscreenArmedRef.current = true;
    try {
      await document.documentElement.requestFullscreen();
    } catch {
      showWarning(
        "Mode layar penuh belum aktif. Gunakan menu browser bila tersedia; tes tetap dapat dilanjutkan."
      );
    }
  }, [showWarning]);

  const returnToFullscreen = useCallback(async () => {
    if (!enabledRef.current) return;
    if (document.fullscreenElement) {
      setFullscreenPrompt(false);
      return;
    }
    fullscreenArmedRef.current = true;
    try {
      await document.documentElement.requestFullscreen();
      setFullscreenPrompt(false);
    } catch {
      showWarning(
        "Layar penuh belum aktif. Tekan tombolnya sekali lagi. Tes tetap berjalan."
      );
    }
  }, [showWarning]);

  useEffect(() => {
    const active = consent.enabled && running;
    if (!active) {
      // Keep presenceRef across brief running/consent flicker so re-attach does not
      // open another tab_away for the same leave. Session change clears it above.
      setIsAway(false);
      return;
    }

    const updatePresence = (signal: "away" | "return") => {
      const transition = transitionPresence(
        presenceRef.current,
        signal,
        Date.now()
      );
      if (!transition.changed) return;
      presenceRef.current = transition.state;
      setIsAway(signal === "away");

      if (signal === "away") {
        enqueue(transition.events);
        const clientEventId =
          transition.events[0]?.client_event_id ??
          transition.state.awayClientEventId ??
          createUuid();
        const event = {
          phase: "away" as const,
          product,
          sessionId: sessionId ?? 0,
          sessionKind: normalizedKind,
          clientEventId,
          occurredAt: new Date(
            transition.state.awaySince ?? Date.now()
          ).toISOString(),
        };
        onStateEventRef.current?.(event);
        emitStateEvent(event);
        return;
      }

      enqueue(transition.events);
      const returnEvent = transition.events[0];
      const event = {
        phase: "return" as const,
        product,
        sessionId: sessionId ?? 0,
        sessionKind: normalizedKind,
        clientEventId: returnEvent?.client_event_id ?? "",
        occurredAt: returnEvent?.occurred_at ?? new Date().toISOString(),
      };
      onStateEventRef.current?.(event);
      emitStateEvent(event);
      void onReturnSyncRef.current();
    };

    // visibilitychange only — blur/focus double-count with tab switches and with
    // snapshot/camera focus churn while document.hidden is already true.
    const onVisibilityChange = () =>
      updatePresence(document.hidden ? "away" : "return");

    document.addEventListener("visibilitychange", onVisibilityChange);
    if (document.hidden) {
      if (presenceRef.current.awaySince === null) {
        updatePresence("away");
      } else {
        setIsAway(true);
      }
    } else {
      updatePresence("return");
    }
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [
    consent.enabled,
    enqueue,
    normalizedKind,
    product,
    running,
    sessionId,
  ]);

  useEffect(() => {
    if (!consent.enabled) return;

    const onFullscreenChange = () => {
      const fullscreen = Boolean(document.fullscreenElement);
      if (fullscreen === fullscreenStateRef.current) return;
      fullscreenStateRef.current = fullscreen;
      if (!fullscreenArmedRef.current) return;
      if (fullscreen) {
        recordEvent("fullscreen_enter", { fullscreen: true });
        setFullscreenPrompt(false);
      } else {
        recordEvent("fullscreen_exit", { fullscreen: false });
        setFullscreenPrompt(true);
      }
    };

    const onKeyDown = (event: KeyboardEvent) => {
      const key = event.key.toLowerCase();
      const macCapture =
        event.metaKey && event.shiftKey && ["3", "4", "5"].includes(key);
      const restrictedShortcut =
        event.key === "PrintScreen" ||
        event.key === "F12" ||
        macCapture ||
        ((event.ctrlKey || event.metaKey) &&
          ["p", "s", "u"].includes(key));
      if (!restrictedShortcut) return;

      event.preventDefault();
      const shortcut = [
        event.ctrlKey ? "Ctrl" : "",
        event.metaKey ? "Meta" : "",
        event.shiftKey ? "Shift" : "",
        event.key,
      ]
        .filter(Boolean)
        .join("+");
      recordEvent("shortcut_warning", { shortcut });
      showWarning(
        event.key === "PrintScreen" || macCapture
          ? "Tangkapan layar dibatasi selama tes."
          : "Pintasan ini tidak tersedia selama tes."
      );
    };

    document.addEventListener("fullscreenchange", onFullscreenChange);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("fullscreenchange", onFullscreenChange);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [consent.enabled, recordEvent, showWarning]);

  useEffect(() => {
    const onOnline = () => void flushQueue();
    window.addEventListener("online", onOnline);
    const interval = window.setInterval(() => void flushQueue(), 15000);
    return () => {
      window.removeEventListener("online", onOnline);
      window.clearInterval(interval);
      window.clearTimeout(retryTimerRef.current);
      window.clearTimeout(warningTimerRef.current);
    };
  }, [flushQueue]);

  return {
    ready: consent.ready,
    enabled: consent.enabled,
    accepted: !consent.enabled || consent.row?.accepted === true,
    accepting: consent.accepting,
    consentError: consent.error,
    policyVersion: consent.row?.policy_version,
    isAway,
    warning,
    fullscreenPrompt,
    acceptConsent,
    requestFullscreen,
    returnToFullscreen,
    recordEvent,
    recordSnapshotFailure,
  };
}
